import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  parseApiError,
  type ClanScopedRole,
  type OrganizationRole,
  type RolePolicyBundle,
  type RolePolicyScope,
} from '../lib/api';
import { cn } from '../lib/cn';
import { Card } from './Card';
import { PageSpinner } from './Spinner';
import { MORPH } from '../lib/motion';
import { Crown, ShieldCheck } from 'lucide-react';

function ErrorMsg({ msg }: { msg: string }) {
  return msg ? <p className="mt-2 text-xs text-red-400">{msg}</p> : null;
}

// Duplicated on purpose from Organizations.tsx — that page still owns
// OrgMemberRow/CompetitionMemberRow/ClanMemberRow which need the same role
// option lists, so the arrays weren't removed from there.
//
// COMP SCOPE RETIRED (2026-08-02): the COMP row is gone from this editor.
// The role spine folded every comp capability into org.* at ADMIN
// (src/shared/roleSpine.ts's CAPABILITY_MIN_RANK has no comp.* entries at
// all), so a COMP-scoped policy record is written to a key nothing reads.
// A founder toggling switches that change nothing is worse than no control:
// it reports success for a policy the platform will never enforce.
const ORG_ROLE_OPTIONS: OrganizationRole[] = ['ORG_OWNER', 'ORG_ADMIN', 'ORG_MEMBER', 'ORG_VIEWER'];
const CLAN_ROLE_OPTIONS: ClanScopedRole[] = ['CLAN_OWNER', 'CLAN_LEADER', 'CLAN_MEMBER'];

// Moved out of Organizations.tsx (org-step-segmentation consolidation) —
// platform-wide (not per-org) role capability switches. Founder-only.
export function RolePolicySection({
  isFounder,
}: {
  isFounder: boolean;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['rolePolicies'],
    queryFn: api.admin.rolePolicies,
    enabled: isFounder,
  });
  if (!isFounder) return null;
  if (isLoading || !data) return <PageSpinner />;

  return (
    <Card>
      <div className="mb-4 flex items-center gap-2">
        <Crown className="h-4 w-4 text-ink-secondary" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-meta">Role Capability Switches</h2>
      </div>
      <div className="space-y-4">
        {(['ORG', 'CLAN'] as RolePolicyScope[]).map(scope => (
          <RolePolicyScopeEditor
            key={scope}
            scope={scope}
            bundle={data}
            onDone={() => qc.invalidateQueries({ queryKey: ['rolePolicies'] })}
          />
        ))}
      </div>
    </Card>
  );
}

function RolePolicyScopeEditor({
  scope,
  bundle,
  onDone,
}: {
  scope: RolePolicyScope;
  bundle: RolePolicyBundle;
  onDone: () => void;
}) {
  const roles = scope === 'ORG' ? ORG_ROLE_OPTIONS : CLAN_ROLE_OPTIONS;

  return (
    <div className="rounded-lg bg-white/[0.04] p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-meta">{scope} Role Policies</p>
      <div className="space-y-4">
        {roles.map(role => (
          <RolePolicyRoleEditor key={`${scope}-${role}`} scope={scope} role={role} bundle={bundle} onDone={onDone} />
        ))}
      </div>
    </div>
  );
}

function RolePolicyRoleEditor({
  scope,
  role,
  bundle,
  onDone,
}: {
  scope: RolePolicyScope;
  role: string;
  bundle: RolePolicyBundle;
  onDone: () => void;
}) {
  const record = bundle.records.find(item => item.scope === scope && item.role === role);
  const [disabled, setDisabled] = useState<string[]>(record?.disabledCapabilities || []);
  const [error, setError] = useState('');
  const caps = bundle.defaults[scope] || [];
  const mut = useMutation({
    mutationFn: () => api.admin.setRolePolicy(scope, role, disabled),
    onSuccess: () => {
      setError('');
      onDone();
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  useEffect(() => {
    setDisabled(record?.disabledCapabilities || []);
  }, [record]);

  return (
    <div className="rounded-lg bg-paper-inset p-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-ink-primary">{role}</p>
        <button className={cn('btn-primary inline-flex items-center gap-2', MORPH)} onClick={() => mut.mutate()} disabled={mut.isPending}>
          <ShieldCheck className="h-4 w-4" />
          {mut.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {caps.map(capability => {
          const checked = !disabled.includes(capability);
          return (
            <label key={capability} className="flex items-center gap-2 rounded-md bg-white/[0.04] px-3 py-2 text-xs text-ink-body">
              <input
                type="checkbox"
                checked={checked}
                onChange={e => {
                  setDisabled(current =>
                    e.target.checked
                      ? current.filter(item => item !== capability)
                      : [...current, capability],
                  );
                }}
              />
              <span>{capability}</span>
            </label>
          );
        })}
      </div>
      <ErrorMsg msg={error} />
    </div>
  );
}
